import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import {
  createEmptyRecipe,
  convertToSeconds,
  secondsToTime,
  normalizeDietary,
  DIETARY_TAGS,
  DIETARY_LABEL_KEYS,
  DIFFICULTY_LABEL_KEYS,
  RecipeDifficulty,
  type DietaryTag,
  useT,
  type Ingredient,
  type MethodStep,
  type Recipe,
} from "@reciperoll/core";
import { Star, X } from "react-feather";
import { useAuth } from "../lib/auth";
import { uploadRecipeImage } from "../utils/uploadRecipeImage";
import { RecipeIngredientEditor } from "./RecipeIngredientEditor";
import { RecipeMethodEditor } from "./RecipeMethodEditor";
import { StorageImage } from "./StorageImage";
import {
  newIngredientRow,
  newMethodRow,
  ingredientRowsToEntities,
  methodRowsToEntities,
  type IngredientRow,
  type MethodRow,
} from "../utils/recipeFormRows";

interface RecipeFormProps {
  // Pre-assigned id (so an uploaded/imported image under it matches on save).
  recipeId: string;
  mode: "create" | "edit";
  // Scalar recipe fields to prefill (import draft, or the recipe being edited).
  // On edit, non-form fields (favourite, cooked, createdAt…) are preserved from here.
  initialRecipe?: Partial<Recipe>;
  initialIngredients?: IngredientRow[];
  initialMethod?: MethodRow[];
  onSubmit: (recipe: Recipe, ingredients: Ingredient[], methodSteps: MethodStep[]) => void;
  onCancel: () => void;
}

const inputClass =
  "w-full rounded-lg border border-line bg-surface dark:bg-surface-2 px-3 py-2 text-ink outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta";
const labelClass = "block text-sm font-semibold text-ink mb-1";

// Imperative handle so the New/Edit pages can accurately ask whether the form has
// unsaved changes before discarding (rather than guessing from bubbled events,
// which miss button-only edits like rating, dietary, tags, and image removal).
export interface RecipeFormHandle {
  isDirty: () => boolean;
}

// A stable, order-independent fingerprint of the editable form state. Compared
// against the value captured at mount to tell whether anything meaningfully changed.
function formSignature(v: {
  title: string;
  description: string;
  image: string;
  source: string;
  sourceUrl: string;
  hours: string;
  minutes: string;
  difficulty: RecipeDifficulty | "na";
  servings: string;
  dietary: DietaryTag[];
  rating: number | null;
  tags: string[];
  notes: string;
  ingredientRows: IngredientRow[];
  methodRows: MethodRow[];
}) {
  return JSON.stringify({
    title: v.title.trim(),
    description: v.description.trim(),
    image: v.image.trim(),
    source: v.source.trim(),
    sourceUrl: v.sourceUrl.trim(),
    hours: Number(v.hours) || 0,
    minutes: Number(v.minutes) || 0,
    difficulty: v.difficulty,
    servings: v.servings.trim(),
    dietary: [...v.dietary].sort(),
    rating: v.rating,
    tags: [...v.tags].sort(),
    notes: v.notes.trim(),
    ingredients: v.ingredientRows,
    method: v.methodRows,
  });
}

export const RecipeForm = forwardRef<RecipeFormHandle, RecipeFormProps>(function RecipeForm(
  { recipeId, mode, initialRecipe, initialIngredients, initialMethod, onSubmit, onCancel },
  ref,
) {
  const t = useT();
  const { session } = useAuth();
  const userId = session?.user.id;

  const initialTime = secondsToTime(initialRecipe?.cookingDuration ?? 0);

  const [title, setTitle] = useState(initialRecipe?.title ?? "");
  const [description, setDescription] = useState(initialRecipe?.description ?? "");
  const [image, setImage] = useState(initialRecipe?.image ?? "");
  const [source, setSource] = useState(initialRecipe?.source ?? "");
  const [sourceUrl, setSourceUrl] = useState(initialRecipe?.sourceUrl ?? "");
  const [hours, setHours] = useState<string>(
    String((initialTime.days ?? 0) * 24 + (initialTime.hours ?? 0)),
  );
  const [minutes, setMinutes] = useState<string>(String(initialTime.minutes || 0));
  const [difficulty, setDifficulty] = useState<RecipeDifficulty | "na">(
    initialRecipe?.difficulty === null
      ? "na"
      : (initialRecipe?.difficulty ?? RecipeDifficulty.Easy),
  );
  const [servings, setServings] = useState<string>(
    initialRecipe?.servings === null ? "" : String(initialRecipe?.servings ?? 1),
  );
  const [dietary, setDietary] = useState<DietaryTag[]>(initialRecipe?.dietary ?? []);
  const [rating, setRating] = useState<number | null>(initialRecipe?.rating ?? null);
  const [tags, setTags] = useState<string[]>(initialRecipe?.tags ?? []);
  const [tagDraft, setTagDraft] = useState("");
  const [ingredientRows, setIngredientRows] = useState<IngredientRow[]>(
    initialIngredients?.length ? initialIngredients : [newIngredientRow()],
  );
  const [methodRows, setMethodRows] = useState<MethodRow[]>(
    initialMethod?.length ? initialMethod : [newMethodRow()],
  );
  const [notes, setNotes] = useState(initialRecipe?.notes ?? "");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dirty tracking: fingerprint the current editable state and compare against the
  // value captured on first render (the pristine baseline). The parent remounts
  // this form when loading a new draft, so the baseline resets automatically.
  const currentSig = formSignature({
    title, description, image, source, sourceUrl, hours, minutes, difficulty,
    servings, dietary, rating, tags, notes, ingredientRows, methodRows,
  });
  const baselineSig = useRef<string | null>(null);
  if (baselineSig.current === null) baselineSig.current = currentSig;
  useImperativeHandle(
    ref,
    () => ({ isDirty: () => currentSig !== baselineSig.current }),
    [currentSig],
  );

  const toggleDietary = (tag: DietaryTag) =>
    setDietary((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );

  const addTag = () => {
    const tag = tagDraft.trim();
    if (!tag) return;
    if (!tags.some((x) => x.toLowerCase() === tag.toLowerCase())) setTags([...tags, tag]);
    setTagDraft("");
  };
  const removeTag = (tag: string) =>
    setTags((prev) => prev.filter((t) => t !== tag));

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !userId) return;
    setUploading(true);
    setError(null);
    try {
      setImage(await uploadRecipeImage(userId, recipeId, file));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("form.imageUploadFailed"));
      Sentry.captureException(err, {
        tags: { area: "storage", op: "image-upload", bucket: "recipe-images" },
      });
    } finally {
      setUploading(false);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) {
      setError(t("form.giveTitle"));
      return;
    }

    const totalSeconds = convertToSeconds({
      hours: Number(hours) || 0,
      minutes: Number(minutes) || 0,
    });
    const servingsNum = servings.trim() === "" ? null : Number(servings);

    const recipe: Recipe = {
      ...createEmptyRecipe({ id: recipeId }),
      ...(initialRecipe ?? {}), // preserve favourite/cooked/createdAt/etc. on edit
      id: recipeId,
      title: title.trim(),
      description: description.trim(),
      image: image.trim(),
      source: source.trim(),
      sourceUrl: sourceUrl.trim(),
      cookingDuration: totalSeconds > 0 ? totalSeconds : undefined,
      difficulty: difficulty === "na" ? null : difficulty,
      servings: servingsNum && servingsNum > 0 ? servingsNum : null,
      dietary: normalizeDietary(dietary),
      rating,
      tags,
      notes: notes.trim(),
    };

    // Row ids are preserved so edits update rows in place (removed rows tombstone).
    const ingredients = ingredientRowsToEntities(ingredientRows, recipeId);
    const methodSteps = methodRowsToEntities(methodRows, recipeId);

    onSubmit(recipe, ingredients, methodSteps);
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl font-extrabold text-ink">
          {mode === "edit" ? t("recipe.editRecipe") : t("nav.newRecipe")}
        </h1>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl px-4 py-2 text-sm font-semibold text-ink-soft cursor-pointer hover:text-ink"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            className="rounded-xl bg-terracotta hover:bg-terracotta-dark transition-colors px-5 py-2 text-sm font-bold text-white"
          >
            {mode === "edit" ? t("form.saveChanges") : t("form.saveRecipe")}
          </button>
        </div>
      </div>

      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

      <div className="space-y-5">
        <div>
          <label className={labelClass}>{t("form.image")}</label>
          <div className="flex items-center gap-4">
            <div className="w-24 h-24 shrink-0 rounded-xl bg-surface-2 overflow-hidden flex items-center justify-center">
              <StorageImage
                stored={image}
                fallback={<span className="text-xs text-ink-faint">{t("form.noImage")}</span>}
              />
            </div>
            <div className="flex-1 flex items-center gap-2">
              <label className="inline-block cursor-pointer rounded-lg bg-surface-2 hover:bg-line px-4 py-2 text-sm font-semibold text-ink">
                {uploading ? t("form.uploading") : image ? t("form.replaceImage") : t("form.uploadImage")}
                <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} />
              </label>
              {image && !uploading && (
                <button
                  type="button"
                  onClick={() => setImage("")}
                  className="rounded-lg px-3 py-2 text-sm font-semibold text-ink-soft hover:text-red-600 cursor-pointer"
                >
                  {t("common.remove")}
                </button>
              )}
            </div>
          </div>
        </div>

        <div>
          <label className={labelClass}>{t("form.title")}</label>
          <input
            className={inputClass}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("form.titlePlaceholder")}
            autoFocus
          />
        </div>

        <div>
          <label className={labelClass}>{t("form.description")}</label>
          <textarea
            className={`${inputClass} text-sm`}
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("form.descriptionPlaceholder")}
          />
        </div>

        <div>
          <label className={labelClass}>{t("form.source")}</label>
          <input
            className={inputClass}
            value={source}
            onChange={(e) => setSource(e.target.value)}
            placeholder={t("form.sourcePlaceholder")}
          />
        </div>

        <div>
          <label className={labelClass}>{t("form.sourceUrl")}</label>
          <input
            className={inputClass}
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            placeholder={t("form.sourceUrlPlaceholder")}
          />
        </div>

        <div>
          <label className={labelClass}>{t("recipe.difficulty")}</label>
          <select
            className={inputClass}
            value={difficulty}
            onChange={(e) => setDifficulty(e.target.value as RecipeDifficulty | "na")}
          >
            <option value={RecipeDifficulty.Easy}>{t(DIFFICULTY_LABEL_KEYS[RecipeDifficulty.Easy])}</option>
            <option value={RecipeDifficulty.Medium}>{t(DIFFICULTY_LABEL_KEYS[RecipeDifficulty.Medium])}</option>
            <option value={RecipeDifficulty.Hard}>{t(DIFFICULTY_LABEL_KEYS[RecipeDifficulty.Hard])}</option>
            <option value="na">{t("common.na")}</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>{t("recipe.servings")}</label>
            <input
              type="number"
              min={0}
              className={inputClass}
              value={servings}
              onChange={(e) => setServings(e.target.value)}
              placeholder={t("common.na")}
            />
          </div>

          <div>
            <label className={labelClass}>{t("form.cookingTime")}</label>
            <div className="flex items-center gap-2">
              <input type="number" min={0} className={inputClass} value={hours} onChange={(e) => setHours(e.target.value)} />
              <span className="text-sm text-ink-soft">{t("form.hrAbbrev")}</span>
              <input type="number" min={0} className={inputClass} value={minutes} onChange={(e) => setMinutes(e.target.value)} />
              <span className="text-sm text-ink-soft">{t("form.minAbbrev")}</span>
            </div>
          </div>
        </div>

        <div>
          <label className={labelClass}>{t("form.dietary")}</label>
          <div className="flex flex-wrap gap-2">
            {DIETARY_TAGS.map(({ tag }) => {
              const active = dietary.includes(tag);
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleDietary(tag)}
                  className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${active ? "bg-terracotta text-white" : "bg-surface-2 text-ink-soft hover:bg-surface-2"
                    }`}
                >
                  {t(DIETARY_LABEL_KEYS[tag])}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className={labelClass}>{t("form.tags")}</label>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1.5 rounded-full bg-terracotta px-3 py-1.5 text-sm font-medium text-white"
                >
                  {tag}
                  <button
                    type="button"
                    aria-label={t("form.removeTagLabel", { tag })}
                    onClick={() => removeTag(tag)}
                    className="cursor-pointer opacity-80 hover:opacity-100"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <input
            className={inputClass}
            value={tagDraft}
            onChange={(e) => setTagDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addTag();
              }
            }}
            placeholder={t("form.tagPlaceholderWeb")}
          />
        </div>

        <div>
          <label className={labelClass}>{t("recipe.ingredients")}</label>
          <RecipeIngredientEditor rows={ingredientRows} onChange={setIngredientRows} />
        </div>

        <div>
          <label className={labelClass}>{t("recipe.method")}</label>
          <RecipeMethodEditor rows={methodRows} onChange={setMethodRows} />
        </div>

        <div>
          <label className={labelClass}>{t("recipe.notes")}</label>
          <textarea
            className={`${inputClass} text-sm`}
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        <div>
          <label className={labelClass}>{t("recipe.rating")}</label>
          <div className="flex gap-1.5">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                aria-label={t("form.starRating", { count: n })}
                onClick={() => setRating(rating === n ? null : n)}
                className="cursor-pointer"
              >
                <Star
                  className={`w-7 h-7 ${
                    (rating ?? 0) >= n
                      ? "text-terracotta fill-terracotta"
                      : "text-ink-faint"
                  }`}
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </form>
  );
});
